import React, { useEffect, useState } from 'react';
import { doc, updateDoc, query, where, collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase';
import { toast } from 'react-toastify';
import { getAuth } from 'firebase/auth';
import CloseJobModal from './CloseJobModal';
import ReOpenJobModal from './ReOpenJobModal';
import { Button, Badge, Spinner } from '../../UI';
import logger from '../../../utils/logger';

const formatDate = (date) => {
  const options = { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric' };
  return new Date(date).toLocaleString(undefined, options);
};

const JobHeader = ({ jobCard }) => {
  const [isFullyPaid, setIsFullyPaid] = useState(false);
  const [areTasksCompleted, setAreTasksCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReopenModalOpen, setIsReopenModalOpen] = useState(false);
  const [exitMileage, setExitMileage] = useState('');
  const [jobClosureComments, setJobClosureComments] = useState('');
  const [nextServiceMileage, setNextServiceMileage] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [reOpenReason, setReOpenReason] = useState('');
  const entryMileage = jobCard?.entryMileage || '';
  const isJobClosed = jobCard?.status === 'Closed';
  const auth = getAuth();
  const user = auth.currentUser;

  useEffect(() => {
    if (jobCard?.jobCardNo) {
      const invoiceQuery = query(collection(db, 'newInvoices'), where('jobCardNo', '==', jobCard.jobCardNo));
      const unsubscribeInvoices = onSnapshot(invoiceQuery, (snapshot) => {
        const invoices = snapshot.docs.map(doc => doc.data());
        const fullyPaidInvoice = invoices.find(invoice => invoice.invoiceStatus === 'Fully Paid');
        setIsFullyPaid(!!fullyPaidInvoice);
        setLoading(false);
      });

      const tasksQuery = query(collection(db, 'tasks'), where('jobCardNo', '==', jobCard.jobCardNo));
      const unsubscribeTasks = onSnapshot(tasksQuery, (snapshot) => {
        const tasks = snapshot.docs.map(doc => doc.data());
        const allTasksCompleted = tasks.every(task => task.status === 'completed');
        setAreTasksCompleted(allTasksCompleted);
      });

      return () => {
        unsubscribeInvoices();
        unsubscribeTasks();
      };
    }
  }, [jobCard?.jobCardNo]);

  const handleCloseJob = async () => {
    if (parseInt(exitMileage) < parseInt(entryMileage)) {
      toast.error('Exit mileage cannot be lower than entry mileage.');
      return;
    }

    try {
      const jobRef = doc(db, 'jobs', jobCard.id);
      await updateDoc(jobRef, {
        exitMileage,
        jobClosureComments,
        nextServiceMileage,
        deliveryDate: new Date(deliveryDate),
        status: 'Closed',
      });
      toast.success('Job closed successfully');
      setIsModalOpen(false); // Close the modal
    } catch (error) {
      logger.error('Error closing job:', error);
      toast.error('Failed to close job');
    }
  };

  const handleReOpenJob = async () => {
    if (!reOpenReason) {
      toast.error('Please select a reason for reopening the job');
      return;
    }

    try {
      setLoading(true);
      const jobRef = doc(db, 'jobs', jobCard.id);
      await updateDoc(jobRef, {
        status: 'Open',
        reOpenReason: reOpenReason,
        reOpenDate: new Date(),
        reOpenUser: user?.email || 'Unknown User',
      });
      toast.success('Job reopened successfully');
      setIsReopenModalOpen(false);
    } catch (error) {
      logger.error('Error reopening job:', error);
      toast.error('Failed to reopen job');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'exitMileage') {
      setExitMileage(value);
    } else if (name === 'nextServiceMileage') {
      setNextServiceMileage(value);
    }
  };

  const renderOdometerInput = (name, value) => (
    <div className="flex space-x-1">
      {Array.from({ length: 6 }).map((_, index) => (
        <input
          key={index}
          type="text"
          maxLength="1"
          value={value[index] || ''}
          onChange={(e) => {
            const newValue = value.split('');
            newValue[index] = e.target.value;
            handleInputChange({ target: { name, value: newValue.join('') } });
          }}
          className="w-12 h-12 text-center border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xl font-mono"
        />
      ))}
    </div>
  );

  return (
    <div className="bg-white border-b border-gray-200">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
        {/* Customer & Vehicle Details */}
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">
              {jobCard?.customerName}
            </h2>
            {jobCard?.customerEmail && (
              <p className="text-sm text-gray-600">{jobCard?.customerEmail}</p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3">
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm font-medium text-gray-600">Vehicle</span>
              <span className="text-sm text-gray-900 font-medium">
                {jobCard?.vehicleMake} {jobCard?.vehicleModel}
              </span>
            </div>

            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm font-medium text-gray-600">Registration</span>
              <span className="text-sm text-gray-900 font-semibold bg-gray-100 px-3 py-1 rounded">
                {jobCard?.registrationNumber}
              </span>
            </div>

            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm font-medium text-gray-600">VIN</span>
              <span className="text-sm text-gray-900 font-mono">{jobCard?.VIN}</span>
            </div>

            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm font-medium text-gray-600">Entry Mileage</span>
              <span className="text-sm text-gray-900 font-medium">{jobCard?.entryMileage?.toLocaleString()} km</span>
            </div>

            {jobCard?.exitMileage && (
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm font-medium text-gray-600">Exit Mileage</span>
                <span className="text-sm text-gray-900 font-medium">{jobCard?.exitMileage?.toLocaleString()} km</span>
              </div>
            )}

            {jobCard?.deliveryDate && (
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm font-medium text-gray-600">Delivery Date</span>
                <span className="text-sm text-gray-900">{formatDate(jobCard?.deliveryDate.toDate())}</span>
              </div>
            )}
          </div>
        </div>

        {/* Job Card Details & Actions */}
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">
              Job Card: <span className="text-primary-600">{jobCard?.jobCardNo}</span>
            </h2>
            <p className="text-sm text-gray-600">
              Created {formatDate(jobCard?.createdAt.toDate())}
            </p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between py-2">
              <span className="text-sm font-medium text-gray-600">Status</span>
              <Badge variant={jobCard?.status === 'Open' ? 'success' : 'secondary'} size="lg">
                {jobCard?.status || 'Open'}
              </Badge>
            </div>

            <div className="flex items-center justify-between py-2">
              <span className="text-sm font-medium text-gray-600">Payment Status</span>
              <Badge variant={isFullyPaid ? 'success' : 'warning'} size="lg">
                {loading ? 'Checking...' : isFullyPaid ? 'Fully Paid' : 'Pending'}
              </Badge>
            </div>

            <div className="flex items-center justify-between py-2">
              <span className="text-sm font-medium text-gray-600">Tasks Status</span>
              <Badge variant={areTasksCompleted ? 'success' : 'warning'} size="lg">
                {areTasksCompleted ? 'Completed' : 'In Progress'}
              </Badge>
            </div>

            {jobCard?.reOpenReason && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
                <p className="text-xs font-medium text-yellow-800 mb-1">Re-open Reason</p>
                <p className="text-sm text-yellow-900">{jobCard.reOpenReason}</p>
                {jobCard?.reOpenDate && (
                  <p className="text-xs text-yellow-700 mt-1">
                    Reopened on {formatDate(jobCard.reOpenDate.toDate())}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            {!isJobClosed ? (
              <Button
                disabled={!isFullyPaid || !areTasksCompleted || loading}
                onClick={() => setIsModalOpen(true)}
                variant={isFullyPaid && areTasksCompleted ? 'primary' : 'secondary'}
                className="flex-1"
              >
                {loading ? <Spinner size="sm" /> : 'Close Job'}
              </Button>
            ) : (
              <Button
                onClick={() => setIsReopenModalOpen(true)}
                variant="success"
                className="flex-1"
                disabled={loading}
              >
                {loading ? <Spinner size="sm" /> : 'Reopen Job'}
              </Button>
            )}
          </div>

          {!isJobClosed && (!isFullyPaid || !areTasksCompleted) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs font-medium text-blue-800 mb-1">Requirements to Close Job:</p>
              <ul className="text-xs text-blue-700 space-y-1 ml-4">
                {!isFullyPaid && <li className="list-disc">Invoice must be fully paid</li>}
                {!areTasksCompleted && <li className="list-disc">All tasks must be completed</li>}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Modal Components */}
      <CloseJobModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        exitMileage={exitMileage}
        setExitMileage={setExitMileage}
        jobClosureComments={jobClosureComments}
        setJobClosureComments={setJobClosureComments}
        nextServiceMileage={nextServiceMileage}
        setNextServiceMileage={setNextServiceMileage}
        deliveryDate={deliveryDate}
        setDeliveryDate={setDeliveryDate}
        handleCloseJob={handleCloseJob}
        renderOdometerInput={renderOdometerInput}
      />

      <ReOpenJobModal
        isReopenModalOpen={isReopenModalOpen}
        setIsReopenModalOpen={setIsReopenModalOpen}
        reOpenReason={reOpenReason}
        setReOpenReason={setReOpenReason}
        handleReOpenJob={handleReOpenJob}
      />
    </div>
  );
};

export default JobHeader;
